<!-- The rules, on one screen, in the order you need them. -->
<template>
  <IonPage>
    <IonHeader :translucent="true">
      <IonToolbar>
        <IonButtons slot="start">
          <IonBackButton default-href="/tabs/game" text="Back" />
        </IonButtons>
        <IonTitle>How to play</IonTitle>
      </IonToolbar>
    </IonHeader>

    <IonContent class="rm-content" :fullscreen="true">
      <div class="rm-page doc">
        <p class="lede">
          Nick clears the maze one slice at a time. Raf hunts him. The four fruit
          pies sit in the corners, furthest from safety.
        </p>

        <section class="cast" aria-label="Cast">
          <article>
            <svg class="art" viewBox="-13 -13 26 26" width="42" height="42" aria-hidden="true">
              <path d="M-9.4 8.4 L-9.4 -1 A9.4 9.4 0 0 1 9.4 -1 L9.4 8.4 Z" fill="#ff2d95" />
              <path
                d="M-9.4 8.4 Q-7.5 13 -5.6 8.4 Q-3.8 6.4 -1.9 8.4 Q0 13 1.9 8.4 Q3.8 6.4 5.6 8.4 Q7.5 13 9.4 8.4 Z"
                fill="#ff2d95"
              />
              <circle cx="-3.5" cy="-2.8" r="2.9" fill="#fff" />
              <circle cx="-2.7" cy="-2.8" r="1.4" fill="#0b1020" />
              <circle cx="3.5" cy="-2.8" r="2.9" fill="#fff" />
              <circle cx="4.3" cy="-2.8" r="1.4" fill="#0b1020" />
            </svg>
            <b>Raf</b><span>hunts you</span>
          </article>
          <article>
            <svg class="art" viewBox="-13 -13 26 26" width="42" height="42" aria-hidden="true">
              <circle cx="0" cy="0" r="9.6" fill="#ffd23f" />
              <path d="M0 0 L10.2 -6.1 A10.2 10.2 0 0 1 10.2 6.1 Z" fill="#080b16" />
              <circle cx="-3.2" cy="-4.6" r="2.2" fill="#fff" />
              <circle cx="-2.5" cy="-4.6" r="1.1" fill="#080b16" />
              <circle cx="2.3" cy="-5.2" r="2.2" fill="#fff" />
              <circle cx="3" cy="-5.2" r="1.1" fill="#080b16" />
            </svg>
            <b>Nick</b><span>that&rsquo;s you</span>
          </article>
          <article>
            <svg class="art" viewBox="-13 -13 26 26" width="42" height="42" aria-hidden="true">
              <g transform="rotate(14)">
                <path d="M0 5.1 L-6.1 -2.4 A9.7 9.7 0 0 1 6.1 -2.4 Z" fill="#ffd868" />
                <path
                  d="M-6.1 -2.4 A9.7 9.7 0 0 1 6.1 -2.4 L4.1 -0.8 A6.6 6.6 0 0 0 -4.1 -0.8 Z"
                  fill="#f0a545"
                />
                <circle cx="0" cy="1.5" r="1.45" fill="#d8331f" />
                <circle cx="-2.4" cy="-1.3" r="1.35" fill="#d8331f" />
                <circle cx="2.4" cy="-1.3" r="1.35" fill="#d8331f" />
              </g>
            </svg>
            <b>Pizza</b><span>10 each</span>
          </article>
          <article>
            <svg class="art" viewBox="-13 -13 26 26" width="42" height="42" aria-hidden="true">
              <ellipse cx="0" cy="3" rx="9.5" ry="4.2" fill="#ffb02e" />
              <path d="M-9.5 3 A9.5 9.5 0 0 1 9.5 3 Z" fill="#ff2d95" />
              <path
                d="M-5.6 -2.4 L5.6 -2.4 M-7.6 0.4 L7.6 0.4"
                stroke="#ffe0a6" stroke-width="1.5" stroke-linecap="round"
              />
              <path
                d="M-3.3 3 L-3.3 -5.9 M3.3 3 L3.3 -5.9"
                stroke="#ffe0a6" stroke-width="1.5" stroke-linecap="round"
              />
            </svg>
            <b>Fruit pie</b><span>flips it</span>
          </article>
        </section>

        <h2>The flip</h2>
        <p>
          Eat a fruit pie and it inverts. Raf turns pale and runs, and for a few
          seconds you are the one doing the chasing. Catching him is worth
          <b>200</b> — and <b>400</b>, <b>800</b>, then <b>1,600</b> if you can
          catch him again before the pie wears off.
        </p>
        <p>
          The bar at the top of the screen is the whole tension. When it runs
          low Raf flashes, and the moment it empties he turns back around —
          wherever you happen to be standing.
        </p>

        <h2>Scoring</h2>
        <dl class="scores">
          <div><dt>Pizza slice</dt><dd>10</dd></div>
          <div><dt>Fruit pie</dt><dd>50</dd></div>
          <div><dt>Raf, while he is running</dt><dd>200 → 1,600</dd></div>
        </dl>

        <h2>Getting around</h2>
        <p>
          Swipe anywhere on the maze, use the pad below it, or the arrow keys and
          <b>WASD</b> on a keyboard. Turns are remembered: swipe before the
          corner and Nick takes it when he gets there.
        </p>
        <p>
          The middle row runs off both edges of the maze. Raf will follow you
          through it, but it is the one place you can get behind him.
        </p>

        <h2>Raf</h2>
        <p>
          He does not wander. Raf works out the genuine shortest path through the
          maze to where you are — and on the harder settings, to where you are
          <em>going</em>. What buys you time is that he refuses to double back,
          and that he breaks off every so often to sulk in his corner.
        </p>

        <h2>Difficulty</h2>
        <dl class="scores">
          <div v-for="d in DIFFICULTIES" :key="d.id">
            <dt>{{ d.name }}</dt>
            <dd class="wide">{{ d.blurb }}</dd>
          </div>
        </dl>
        <h2>Pace</h2>
        <p>
          Separate from difficulty. <b>Slow</b>, <b>Normal</b> and <b>Fast</b>
          scale everything by the same amount &mdash; Nick, every Raf, the waves
          and the fruit-pie timer. Nothing about the game changes except how fast
          it happens, so a slow run is the same chase with more time to see it.
        </p>
        <p class="fine">
          Best scores are kept separately for each combination of difficulty,
          pace, map, Rafs and pies, because they are not the same game.
        </p>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonBackButton,
} from '@ionic/vue';
import { DIFFICULTIES } from '../store.js';

</script>

<style scoped>
.doc { padding: 8px 20px 40px; max-width: 34rem; margin: 0 auto; }
.lede { font-size: 16px; line-height: 1.55; margin: 4px 0 22px; color: var(--rm-ink); }

.cast { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 30px; }
.cast article {
  display: flex; flex-direction: column; align-items: center; gap: 5px;
  padding: 12px 4px; text-align: center;
  background: var(--rm-panel); border: 1px solid var(--rm-edge); border-radius: 9px;
}
.cast b { font-size: 10px; font-weight: 700; letter-spacing: 0.04em; }
.cast span:last-child { font-size: 10px; line-height: 1.35; color: var(--rm-dim); }
svg.art { display: block; }

h2 {
  font-size: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--rm-cyan); margin: 30px 0 10px;
}
p { font-size: 14px; line-height: 1.6; color: var(--rm-dim); margin: 0 0 12px; }
p b { color: var(--rm-gold); font-weight: 700; }
p em { color: var(--rm-ink); font-style: italic; }
.fine { font-size: 12px; margin-top: 14px; }

.scores { margin: 0; }
.scores div {
  display: flex; justify-content: space-between; align-items: baseline; gap: 20px;
  padding: 11px 0; border-bottom: 1px solid var(--rm-edge);
}
dt { font-size: 14px; color: var(--rm-ink); flex: none; }
dd { margin: 0; font-size: 14px; font-weight: 700; color: var(--rm-gold); font-variant-numeric: tabular-nums; }
dd.wide { font-weight: 400; font-size: 12px; color: var(--rm-dim); text-align: right; }
</style>
